import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

// `category` is null when the AI classifier produced nothing — surfaced under an
// "Uncategorized" bucket. `label` is resolved server-side from the classifier's
// canonical definitions.
export const categoryCountSchema = z.object({
  category: z.string().nullable(),
  label: z.string(),
  count: z.number().int().min(0),
});

export const feedbackTypeCountSchema = z.object({
  feedbackType: z.string().nullable(),
  label: z.string(),
  count: z.number().int().min(0),
});

export const dayCountSchema = z.object({
  day: z.string(), // ISO date (YYYY-MM-DD), UTC
  count: z.number().int().min(0),
});

export const categoryOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

// Trailing 7 days vs the 7 days before (created_at based), for the stat-card
// week-over-week trend chips. Ignores the page's date-range filter.
const weeklyTrendPairSchema = z.object({
  current: z.number().int().min(0),
  previous: z.number().int().min(0),
});

export const weeklyTrendSchema = z.object({
  total: weeklyTrendPairSchema,
  unresolved: weeklyTrendPairSchema,
  notContacted: weeklyTrendPairSchema,
});

export const getProductFeedbackAnalytics = unaryRpc({
  name: "getProductFeedbackAnalytics" as const,
  input: z.object({
    startDate: z.date().nullish(),
    endDate: z.date().nullish(),
    teamId: z.string().nullish(),
    // A category key, or "uncategorized" for the null bucket. Valid keys are
    // surfaced to the UI via `categoryOptions`; the handler ignores unknowns.
    category: z.string().nullish(),
  }),
  output: z.object({
    // `*` values reflect the range + category filter; `range*` ignore the
    // category filter so the top stats can show "x / n" for a selected category.
    total: z.number().int().min(0),
    rangeTotal: z.number().int().min(0),
    unresolvedCount: z.number().int().min(0),
    rangeUnresolvedCount: z.number().int().min(0),
    notContactedCount: z.number().int().min(0),
    rangeNotContactedCount: z.number().int().min(0),
    // Range-only screenshot attach metrics (screenshot_s3_key).
    screenshotAttachedCount: z.number().int().min(0),
    screenshotAttachRate: z.number().min(0).max(1),
    byCategory: z.array(categoryCountSchema),
    byFeedbackType: z.array(feedbackTypeCountSchema),
    byDay: z.array(dayCountSchema),
    // Avg submission→first-contact time (contacted_at - created_at) over
    // contacted rows, range + category filter applied; null when none.
    contactedCount: z.number().int().min(0),
    avgSecondsToContact: z.number().nullable(),
    // Follow-up engagement over rows where the analyzer asked a follow-up
    // question (range + category filter applied).
    followUpAskedCount: z.number().int().min(0),
    followUpAnsweredCount: z.number().int().min(0),
    weeklyTrend: weeklyTrendSchema,
    categoryOptions: z.array(categoryOptionSchema),
  }),
});

export type GetProductFeedbackAnalyticsInput = z.infer<
  typeof getProductFeedbackAnalytics.input
>;
export type GetProductFeedbackAnalyticsOutput = z.infer<
  typeof getProductFeedbackAnalytics.output
>;
